"use client";
import { useState } from "react";
import { PDFDocument, rgb } from "pdf-lib";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { formatCurrency } from "../../../utils/formatCurrency.js";
import { capitalize } from "../plan-de-pagos/capitalize.js";
import { planPagos } from "../../../utils/planPagos.js";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import { db } from "../../../FirebaseConfig.ts";
import { formatCurrencyPDF } from "../../../utils/formatCurrencyPDF.js";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../FirebaseConfig.ts";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);
dayjs.locale("es");

function GenerateReportInvestor({ investor, saldo }) {
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));

  const generarCertificado = async () => {
    setLoading(true);

    try {
      // Obtener mes actual en formato YYYY-MM
      const currentMonth = dayjs().format("YYYY-MM");

      // Verificar si ya existe un reporte para este mes
      // const reportsRef = collection(db, "investor_reports");
      // const q = query(
      //   reportsRef,
      //   where("investorId", "==", investor?.uuid),
      //   where("month", "==", currentMonth)
      // );
      // const existingReports = await getDocs(q);

      // if (!existingReports.empty) {
      //   // Ya existe un reporte para este mes, solo abrirlo
      //   const existingReport = existingReports.docs[0].data();
      //   window.open(existingReport.url, "_blank");
      //   setLoading(false);

      //   return;
      // }

      const requestsCollection = collection(db, "requests");

      const creditsQuery = query(
        requestsCollection,
        where("investorId", "==", investor?.uuid),
        where("status", "==", "aprobado")
      );

      const data = await getDocs(creditsQuery);
      const dataA = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

      const dataArray2 = dataA.filter(
        (el) => el.status === "pagado" || el.status === "aprobado"
      );

      const fechaSeleccionada = dayjs(date, "YYYY-MM-DD").startOf("day");
      const dataArray = dataArray2.filter((el) => {
        if (!el.fechaPagoCompleto) return true; // mostrar si no tiene fecha de pago completo
        const fechaPagoCompleto = dayjs(
          el.fechaPagoCompleto,
          "YYYY-MM-DD"
        ).startOf("day");
        return fechaPagoCompleto.isAfter(fechaSeleccionada); // mostrar solo si aún no estaba totalmente pagado
      });

      // Crear nuevo PDF
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]); // A4
      const { width, height } = page.getSize();

      const font = await pdfDoc.embedFont("Helvetica");
      const fontBold = await pdfDoc.embedFont("Helvetica-Bold");

      // ======= Logo =======
      const logoBytes = await fetch("/logo_lendy.png").then((res) =>
        res.arrayBuffer()
      );
      const logoImage = await pdfDoc.embedPng(logoBytes);
      const logoDims = logoImage.scale(0.1);
      page.drawImage(logoImage, {
        x: 40,
        y: height - 60,
        width: logoDims.width,
        height: logoDims.height,
      });

      // ======= Título =======
      page.drawText("Estado cuenta de créditos", {
        x: 390,
        y: height - 60,
        size: 13,
        font: fontBold,
        color: rgb(0, 0, 0),
      });

      // ======= Caja de información del inversionista =======
      const infoTop = height - 100;
      const boxHeight = 45;
      const boxWidth = width - 80;

      // Fondo gris claro
      page.drawRectangle({
        x: 40,
        y: infoTop - boxHeight,
        width: boxWidth,
        height: boxHeight,
        color: rgb(0.97, 0.97, 0.97),
        borderColor: rgb(0.75, 0.75, 0.75),
        borderWidth: 0.5,
      });

      // Datos del inversionista
      const infoY = infoTop - 15;
      page.drawText("Inversionista:", {
        x: 50,
        y: infoY,
        size: 9,
        font: fontBold,
      });
      page.drawText(`${investor?.name} ${investor?.lastName}`, {
        x: 120,
        y: infoY,
        size: 9,
        font,
      });

      page.drawText("C.C. ", { x: 50, y: infoY - 20, size: 9, font: fontBold });
      page.drawText(`${investor?.identity}`, {
        x: 70,
        y: infoY - 20,
        size: 9,
        font,
      });

      page.drawText("Fecha de corte:", {
        x: 330,
        y: infoY,
        size: 9,
        font: fontBold,
      });
      page.drawText(
        `${dayjs(date).format("YYYY")}/${dayjs(date).format("MM")}/30`,
        {
          x: 420,
          y: infoY,
          size: 9,
          font,
        }
      );

      page.drawText("Fecha límite de pago:", {
        x: 330,
        y: infoY - 20,
        size: 9,
        font: fontBold,
      });
      page.drawText(
        `${dayjs(date).format("YYYY")}/${dayjs(date)
          .add(1, "month")
          .format("MM")}/15`,
        {
          x: 440,
          y: infoY - 20,
          size: 9,
          font,
        }
      );

      // ======= Tabla =======
      const tableTop = infoTop - boxHeight - 40;
      const colWidths = [60, 120, 70, 55, 70, 35, 40, 60, 70];
      const headers = [
        "Documento",
        "Nombre trabajador",
        "No. crédito",
        "T. interés",
        "M. desembolsado",
        "C. Pact",
        "C. Pag",
        "Total a pagar",
      ];

      let currentY = tableTop;
      const rowHeight = 18;
      const tableLeft = 40;
      const tableWidth = colWidths.reduce((a, b) => a + b, 0);

      // ======= Fondo encabezado =======
      page.drawRectangle({
        x: tableLeft,
        y: currentY - rowHeight,
        width: tableWidth - 60,
        height: rowHeight,
        color: rgb(0.93, 0.93, 0.93),
        borderColor: rgb(0.7, 0.7, 0.7),
        borderWidth: 0.5,
      });

      // Encabezados
      let x = tableLeft + 4;
      headers.forEach((header, i) => {
        page.drawText(header, {
          x,
          y: currentY - 12,
          size: 7.5,
          font: fontBold,
          color: rgb(0.1, 0.1, 0.1),
        });
        x += colWidths[i];
      });

      currentY -= rowHeight;

      // ======= Dibujar filas =======
      let totalMes = 0;
      let totalPagar = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const el = dataArray[i];
        const { schedule } = planPagos(el, el.tasaInvestor);
        const monthlyInvestorEarning =
          (el.ammount * el.tasaInvestor) / 100 / 12 || 0;

        const cuotasFiltradas = schedule.filter((s) => {
          const [day, month, year] = s.fechaPago.split("/");
          const fechaCuota = dayjs(`${year}-${month}-30`);
          return (
            fechaCuota.month() === dayjs(date).month() &&
            fechaCuota.year() === dayjs(date).year()
          );
        });

        const pagosHastaFecha =
          el.payments?.filter((p) => {
            const pagoDate = dayjs(p.date, "YYYY-MM-DD").startOf("day");
            return pagoDate.isSameOrBefore(
              dayjs(date, "YYYY-MM-DD").startOf("day")
            );
          }) || [];

        const pagosRealizados = pagosHastaFecha.length;

        const currentPayment = cuotasFiltradas[0] || schedule[0] || {};
        const cuota = currentPayment.cuotaAPagar || 0;

        const row = [
          el.identity || "",
          `${capitalize(el.name)} ${capitalize(el.lastName)}`,
          el.numberCredit || "",
          `${el.tasaInvestor}%`,
          `$${formatCurrency(el.ammount)}`,
          el.month || 0,
          pagosRealizados || el.paymentsInvestor?.length || 0,
          `$${formatCurrencyPDF(cuota)}`,
        ];

        // Fondo alternado
        const isEven = i % 2 === 0;
        page.drawRectangle({
          x: tableLeft,
          y: currentY - rowHeight,
          width: tableWidth - 60,
          height: rowHeight,
          color: isEven ? rgb(1, 1, 1) : rgb(0.985, 0.985, 0.985),
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 0.3,
        });

        x = tableLeft + 4;
        row.forEach((text, j) => {
          page.drawText(String(text), {
            x,
            y: currentY - 12,
            size: 7,
            font,
            color: rgb(0, 0, 0),
          });
          x += colWidths[j];
        });

        totalMes += monthlyInvestorEarning;
        totalPagar += schedule[0]?.cuotaAPagar || 0;
        currentY -= rowHeight;
      }

      // ======= Totales =======
      page.drawRectangle({
        x: tableLeft,
        y: currentY - rowHeight,
        width: tableWidth - 60,
        height: rowHeight,
        color: rgb(0.9, 0.9, 0.9),
        borderColor: rgb(0.6, 0.6, 0.6),
        borderWidth: 0.5,
      });

      page.drawText("TOTALES", {
        x: tableLeft + tableWidth - 190,
        y: currentY - 12,
        size: 8,
        font: fontBold,
      });

      page.drawText(`$${formatCurrencyPDF(totalPagar)}`, {
        x: tableLeft + tableWidth - 130,
        y: currentY - 12,
        size: 8,
        font,
      });

      // ======= Saldo acumulado =======
      page.drawRectangle({
        x: tableLeft,
        y: currentY - rowHeight - 30,
        width: tableWidth - 60,
        height: rowHeight,
        color: rgb(0.9, 0.9, 0.9),
        borderColor: rgb(0.6, 0.6, 0.6),
        borderWidth: 0.5,
      });

      page.drawText("SALDO ACUMULADO", {
        x: tableLeft + tableWidth - 210,
        y: currentY - 42,
        size: 8,
        font: fontBold,
      });

      page.drawText(`$${saldo}`, {
        x: tableLeft + tableWidth - 120,
        y: currentY - 42,
        size: 8,
        font,
      });

      // ======= Generar PDF bytes =======
      const pdfBytes = await pdfDoc.save();

      // ======= Subir a Firebase Storage =======
      // const fileName = `reportes/${investor?.uuid}/${currentMonth}_reporte.pdf`;
      // const storageRef = ref(storage, fileName);
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });

      // await uploadBytes(storageRef, pdfBlob);
      // const downloadURL = await getDownloadURL(storageRef);

      // ======= Guardar metadata en Firestore =======
      // await addDoc(collection(db, "investor_reports"), {
      //   investorId: investor?.uuid,
      //   investorName: `${investor?.name} ${investor?.lastName}`,
      //   investorIdentity: investor?.identity,
      //   month: currentMonth,
      //   url: downloadURL,
      //   fileName: fileName,
      //   saldoAcumulado: saldo,
      //   totalCreditos: dataArray.length,
      //   createdAt: dayjs().toISOString(),
      //   timestamp: new Date(),
      // });

      // ======= Abrir el PDF =======
      const url = URL.createObjectURL(pdfBlob);
      const newWindow = window.open(url, "_blank");
      setTimeout(() => {
        newWindow.document.title = `Reporte ${currentMonth} - ${investor?.name}`;
      }, 100);

      setLoading(false);
    } catch (error) {
      console.error("Error al generar el reporte:", error);
      setLoading(false);
    }
  };

  return (
    <div>
      <div>
        <label
          htmlFor="date"
          className="block mb-2 text-sm font-medium text-gray-700"
        >
          Fecha:
        </label>
        <input
          type="date"
          id="date"
          name="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
        />
      </div>
      <button
        onClick={() => generarCertificado()}
        className={`!mx-0 rounded-lg py-1 px-2 font-semibold mt-3 bg-richBlack text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        disabled={loading}
        aria-disabled={loading}
      >
        {loading ? "Generando reporte..." : "Descargar Reporte"}
      </button>
    </div>
  );
}

export default GenerateReportInvestor;
